import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import * as api from '../../api';
import { getScoresWithPoints, pad } from '../../utils/scoring';
import { toDivisionKey, toCategoryKey } from '../../components/ShooterForm';

export default function MeMatch() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  const BackIcon = isRtl ? ArrowForwardIcon : ArrowBackIcon;
  const ForwardIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon;

  const [match, setMatch] = useState(null);
  const [stages, setStages] = useState(null);
  const [scores, setScores] = useState(null);
  const [shooters, setShooters] = useState(null);

  const saved = JSON.parse(localStorage.getItem('signedIn') || 'null');
  const publicId = saved ? `${saved.countryCode}|${saved.publicId}` : null;

  async function load() {
    setMatch(null);
    setStages(null);
    setScores(null);
    setShooters(null);

    const { data: matchData } = await api.getMatch(matchId);
    setMatch(matchData);

    const stageIds = (matchData.stages || []).filter(Boolean);
    const [stagesRes, scoresRes, shootersRes] = await Promise.all([
      stageIds.length ? api.getStages(stageIds.join(',')) : { data: [] },
      matchData.scoresPublished ? api.getScores(matchId) : { data: [] },
      matchData.scoresPublished ? api.getShooters(matchId) : { data: [] },
    ]);

    setStages(stagesRes.data || []);
    const scoresData = scoresRes.data;
    setScores(Array.isArray(scoresData) ? scoresData : scoresData?.scores || []);
    setShooters(shootersRes.data || []);
  }

  useEffect(() => { load(); }, [matchId]);

  const stagesList = useMemo(
    () => (match?.stages || []).map((sid) => stages?.find((s) => s && s.id?.toString() === sid?.toString())).filter(Boolean),
    [match, stages],
  );

  const { scoresByStage, scoresOverall, dqs } = useMemo(
    () => (scores && stagesList.length > 0 && shooters)
      ? getScoresWithPoints({ scores, stages: stagesList, shooters })
      : { scoresByStage: [], scoresOverall: [], dqs: [] },
    [scores, stagesList, shooters],
  );

  // The signed-in person may have several registrations in one match (one per
  // division): the base publicId plus -N variants (e.g. IL|30116, IL|30116-1).
  const registrations = useMemo(
    () => (shooters || []).filter(
      (s) => s.publicId === publicId || (typeof s.publicId === 'string' && s.publicId.startsWith(`${publicId}-`)),
    ),
    [shooters, publicId],
  );

  if (!match || !stages || !scores || !shooters) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 56px)', bgcolor: '#f5f5f5' }}>
        <CircularProgress thickness={2} size={64} />
      </Box>
    );
  }

  const rounds = stagesList.reduce((sum, s) => sum + (s.rounds || 0), 0);

  function getTrophyColor(pos) {
    if (pos === 1) return '#FFD700';
    if (pos === 2) return '#C0C0C0';
    if (pos === 3) return '#CD7F32';
    return null;
  }

  // Per-registration placement within its own division.
  function getRegResult(registration) {
    const dq = dqs.find((s) => s.shooterId === registration?.id);
    let overallResult = null;
    if (registration && !dq) {
      const scoresDivision = scoresOverall.filter((s) => s.division === registration.division);
      const meIndex = scoresDivision.findIndex((s) => s.shooterId === registration.id);
      if (meIndex > -1) {
        const me = scoresDivision[meIndex];
        const percentage = me.percentage != null
          ? parseInt(me.percentage, 10) / 100
          : scoresDivision.length > 0
            ? Math.round((me.score / scoresDivision[0].score) * 10000) / 100
            : 0;
        overallResult = {
          pos: meIndex + 1,
          total: scoresDivision.length,
          points: pad(me.score, 4),
          percentage,
        };
        // Category placement within the division, for non-regular categories.
        if (me.category && me.category !== 'regular') {
          const scoresCategory = scoresDivision.filter((s) => s.category === me.category);
          overallResult.category = me.category;
          overallResult.categoryPos = scoresCategory.findIndex((s) => s.shooterId === registration.id) + 1;
          overallResult.categoryTotal = scoresCategory.length;
        }
      }
    }
    return {
      dq,
      overallResult,
      trophyColor: overallResult ? getTrophyColor(overallResult.pos) : null,
      categoryTrophyColor: overallResult?.category ? getTrophyColor(overallResult.categoryPos) : null,
    };
  }

  return (
    <Box sx={{ pb: '80px', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero header with cover */}
      <Box sx={{
        position: 'relative',
        backgroundImage: match.cover ? `url(${match.cover})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        bgcolor: match.cover ? undefined : '#0d47a1',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(13,71,161,0.9) 100%)',
          zIndex: 1,
        },
      }}>
        <Box sx={{ position: 'relative', zIndex: 2, px: 2, pt: 1.5, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'white' }}>
                {match.title}
              </Typography>
            </Box>
            <IconButton onClick={load} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Stats chips */}
          <Box sx={{ display: 'flex', gap: 1.5, ml: 6, flexWrap: 'wrap' }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5, backdropFilter: 'blur(4px)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
                {t('matches.stages')}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                {(match.stages || []).length}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5, backdropFilter: 'blur(4px)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
                {t('stages.rounds')}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                {rounds}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5, backdropFilter: 'blur(4px)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
                {t('matches.shooters')}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                {(shooters || []).length}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 1.5, mt: -1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Per-registration result + stage scores (one block per division) */}
        {registrations.map((registration) => {
          const { dq, overallResult, trophyColor, categoryTrophyColor } = getRegResult(registration);
          return (
          <React.Fragment key={registration.id}>
          <Paper sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
            {dq ? (
              <Box sx={{ p: 2.5, bgcolor: '#d32f2f', color: 'white', textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit' }}>
                  {t('app.meResultDQ')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1.5 }}>
                  {dq.dq && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Rule</Typography>
                      <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>{dq.dq}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 2.5, textAlign: 'center' }}>
                {(trophyColor || categoryTrophyColor) && (
                  <EmojiEventsIcon sx={{ fontSize: 40, color: trophyColor || categoryTrophyColor, mb: 1 }} />
                )}
                {overallResult ? (
                  <>
                    <Box dir="ltr" sx={{ textAlign: 'center', mb: 0.5 }}>
                      <Typography component="span" variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {'#'}{overallResult.pos}
                      </Typography>
                      <Typography component="span" variant="body1" sx={{ color: 'text.secondary' }}>
                        {' / '}{overallResult.total}
                      </Typography>
                    </Box>
                    {categoryTrophyColor && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <EmojiEventsIcon sx={{ fontSize: 18, color: categoryTrophyColor }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          <span dir="ltr">{`#${overallResult.categoryPos} / ${overallResult.categoryTotal}`}</span>
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {t(`categories.${toCategoryKey(overallResult.category)}`, { defaultValue: overallResult.category })}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{overallResult.points}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('app.statPts')}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{pad(overallResult.percentage, 2)}%</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('app.statPct')}</Typography>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Typography variant="h6">{t('app.meRegistration')}</Typography>
                )}

                {/* Division / Category / PF */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ bgcolor: grey[100], borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1, fontSize: 10 }}>
                      {t('registration.division')}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {t(`divisions.${toDivisionKey(registration.division)}`, { defaultValue: registration.division })}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: grey[100], borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1, fontSize: 10 }}>
                      {t('registration.category')}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {t(`categories.${toCategoryKey(registration.category)}`, { defaultValue: registration.category })}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: grey[100], borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1, fontSize: 10 }}>
                      {t('registration.powerFactor')}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {t(`registration.${registration['power-factor']}`, { defaultValue: registration['power-factor'] })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Stage scores */}
          {!dq && scoresByStage.length > 0 && (
          <Paper sx={{ borderRadius: 2.5, overflow: 'hidden', mt: 1.5 }}>
            {(match.stages || []).map((_, stageIdx) => {
              const stage = stagesList[stageIdx];
              if (!stage) return null;

              const stageScores = scoresByStage[stageIdx] || [];
              const userScoreIdx = stageScores.findIndex(
                (s) => s.shooterId?.toString() === registration?.id?.toString(),
              );
              const userScore = userScoreIdx >= 0 ? stageScores[userScoreIdx] : null;
              const topFactor = stageScores[0]?.factor || 1;

              return (
                <Box key={stageIdx} sx={{
                  borderTop: stageIdx === 0 ? 'none' : `1px solid ${grey[200]}`,
                  p: 1.5,
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('app.meStageLine', { index: stageIdx + 1, title: stage.title })}
                  </Typography>

                  {!userScore ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('app.meNoResultForStage')}
                    </Typography>
                  ) : (
                    <>
                      {/* Hit breakdown */}
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                        {[
                          { label: 'A', value: userScore.alphas, color: '#4caf50' },
                          { label: 'C', value: userScore.charlies, color: '#2196f3' },
                          { label: 'D', value: userScore.deltas, color: '#ff9800' },
                          { label: 'M', value: userScore.misses, color: '#f44336' },
                          ...(userScore.noShoots ? [{ label: 'NS', value: userScore.noShoots, color: '#9e9e9e' }] : []),
                          ...(userScore.penalties ? [{ label: 'PE', value: userScore.penalties, color: '#9e9e9e' }] : []),
                        ].map((h) => (
                          <Box key={h.label} sx={{
                            bgcolor: `${h.color}15`, borderRadius: 1, px: 0.75, py: 0.25,
                            display: 'flex', alignItems: 'center', gap: 0.5,
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: h.color, fontSize: 11 }}>
                              {h.value}{h.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Stats row */}
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 1, alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {userScore.factorStr}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>{t('app.statHF')}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{userScore.total}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>{t('app.statPts')}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{userScore.timeStr}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>{t('app.statSec')}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {pad(parseInt((userScore.factor * 10000) / topFactor, 10) / 100, 2)}%
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>{t('app.statPct')}</Typography>
                        </Box>
                        <Box sx={{
                          bgcolor: userScoreIdx === 0 ? 'primary.main' : grey[200],
                          color: userScoreIdx === 0 ? 'white' : 'text.secondary',
                          borderRadius: 1, px: 0.75, py: 0.25, fontSize: 11, fontWeight: 700,
                        }}>
                          <span dir="ltr">{`#${userScoreIdx + 1} / ${stageScores.length}`}</span>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Paper>
          )}
          </React.Fragment>
          );
        })}

        {/* Links */}
        <Paper sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <ButtonBase
            onClick={() => navigate(`/${match.alias}`)}
            sx={{ p: 1.5, textAlign: 'start', width: '100%', display: 'flex', alignItems: 'center' }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('app.meToSite')}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('app.meToSiteDesc')}</Typography>
            </Box>
            <ForwardIcon sx={{ color: grey[400] }} />
          </ButtonBase>

          {registrations.map((registration) => (
            <ButtonBase
              key={registration.id}
              onClick={() => navigate(`/${match.alias}/shooter-stats/${registration.id}`)}
              sx={{
                p: 1.5, textAlign: 'start', width: '100%',
                display: 'flex', alignItems: 'center',
                borderTop: `1px solid ${grey[200]}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('app.meToStats')}
                  {registrations.length > 1 && ` – ${t(`divisions.${toDivisionKey(registration.division)}`, { defaultValue: registration.division })}`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('app.meToStatsDesc')}</Typography>
              </Box>
              <ForwardIcon sx={{ color: grey[400] }} />
            </ButtonBase>
          ))}
        </Paper>
      </Box>
    </Box>
  );
}
